package com.trevorwiebe.timesheet.admin.rate.presentation

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material3.Card
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Switch
import androidx.compose.material3.SwitchDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.foundation.text.KeyboardOptions
import com.trevorwiebe.timesheet.core.domain.model.Rate
import com.trevorwiebe.timesheet.core.presentation.TopBar
import com.trevorwiebe.timesheet.core.presentation.common.BackIcon
import com.trevorwiebe.timesheet.core.presentation.common.DialogButtonRow
import com.trevorwiebe.timesheet.core.presentation.common.NativeDialog
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveContent
import com.trevorwiebe.timesheet.core.presentation.common.responsiveDialogWidth
import com.trevorwiebe.timesheet.core.presentation.common.TimesheetTextField
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun RateScreen(
    viewModel: RateViewModel = koinViewModel(),
    onBack: () -> Unit
) {
    val state by viewModel.state.collectAsState()

    Scaffold(
        topBar = {
            TopBar(
                title = "Rates",
                backIcon = { BackIcon() },
                onBack = onBack
            )
        },
        floatingActionButton = {
            FloatingActionButton(
                onClick = { viewModel.onEvent(RateEvent.OnAddRateClick) },
                containerColor = MaterialTheme.colorScheme.tertiary,
                contentColor = MaterialTheme.colorScheme.onTertiary
            ) {
                Icon(Icons.Default.Add, contentDescription = "Add rate")
            }
        }
    ) { paddingValues ->
        ResponsiveContent(modifier = Modifier.padding(paddingValues)) {
        if (state.isLoading) {
            Box(
                modifier = Modifier.fillMaxSize(),
                contentAlignment = Alignment.Center
            ) {
                CircularProgressIndicator(color = MaterialTheme.colorScheme.tertiary)
            }
        } else {
            LazyColumn(
                modifier = Modifier
                    .fillMaxSize()
            ) {
                items(state.rates) { rate ->
                    RateRow(
                        rate = rate,
                        onClick = { viewModel.onEvent(RateEvent.OnEditRateClick(rate)) }
                    )
                }
            }
        }

        if (state.showAddEditDialog) {
            AddEditRateDialog(
                editing = state.editingRate,
                onDismiss = { viewModel.onEvent(RateEvent.OnDismissDialog) },
                onSave = { description, rateCode, overtimeRateCode, defaultRate ->
                    viewModel.onEvent(RateEvent.OnSaveRate(description, rateCode, overtimeRateCode, defaultRate))
                }
            )
        }

        NativeDialog(
            visible = state.error != null,
            title = "Something went wrong",
            message = state.error ?: "",
            confirmText = "OK",
            onConfirm = { viewModel.onEvent(RateEvent.OnErrorShown) },
            onDismiss = { viewModel.onEvent(RateEvent.OnErrorShown) }
        )
        }
    }
}

@Composable
private fun RateRow(
    rate: Rate,
    onClick: () -> Unit
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = rate.description.ifEmpty { "(no name)" },
                    fontSize = 16.sp,
                    fontWeight = FontWeight.SemiBold
                )
                if (rate.rateCode.isNotBlank()) {
                    Text(
                        text = rate.rateCode,
                        fontSize = 14.sp,
                        color = MaterialTheme.colorScheme.secondary
                    )
                }
            }
            if (rate.defaultRate) {
                Text(
                    text = "Default",
                    fontSize = 14.sp,
                    fontWeight = FontWeight.SemiBold,
                    color = MaterialTheme.colorScheme.tertiary
                )
            }
        }
        HorizontalDivider()
    }
}

@Composable
private fun AddEditRateDialog(
    editing: Rate?,
    onDismiss: () -> Unit,
    onSave: (description: String, rateCode: String, overtimeRateCode: String, defaultRate: Boolean) -> Unit
) {
    var description by remember { mutableStateOf(editing?.description ?: "") }
    var rateCode by remember { mutableStateOf(editing?.rateCode ?: "") }
    var overtimeRateCode by remember { mutableStateOf(editing?.overtimeRateCode ?: "") }
    var defaultRate by remember { mutableStateOf(editing?.defaultRate ?: false) }

    Dialog(onDismissRequest = onDismiss) {
        Card(
            modifier = Modifier.responsiveDialogWidth(),
            shape = RoundedCornerShape(16.dp)
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(16.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Text(
                    text = if (editing == null) "Add Rate" else "Edit Rate",
                    fontSize = 20.sp,
                    fontWeight = FontWeight.Bold
                )
                Spacer(modifier = Modifier.height(16.dp))

                TimesheetTextField(
                    text = description,
                    label = "Rate name",
                    keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Words),
                    onTextChange = { description = it }
                )
                Spacer(modifier = Modifier.height(8.dp))

                TimesheetTextField(
                    text = rateCode,
                    label = "Rate code",
                    keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Characters),
                    onTextChange = { rateCode = it }
                )
                Text(
                    text = "Payroll code used in the employee hours export (e.g. REG).",
                    fontSize = 12.sp,
                    color = MaterialTheme.colorScheme.secondary,
                    modifier = Modifier.fillMaxWidth()
                )
                Spacer(modifier = Modifier.height(8.dp))

                TimesheetTextField(
                    text = overtimeRateCode,
                    label = "Overtime rate code",
                    keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Characters),
                    onTextChange = { overtimeRateCode = it }
                )
                Text(
                    text = "Payroll code for hours over 40 per week on this rate (e.g. REGOT).",
                    fontSize = 12.sp,
                    color = MaterialTheme.colorScheme.secondary,
                    modifier = Modifier.fillMaxWidth()
                )
                Spacer(modifier = Modifier.height(8.dp))

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text("Default rate", fontSize = 16.sp)
                    Switch(
                        checked = defaultRate,
                        onCheckedChange = { defaultRate = it },
                        colors = SwitchDefaults.colors(
                            checkedThumbColor = MaterialTheme.colorScheme.tertiary,
                            checkedTrackColor = MaterialTheme.colorScheme.tertiary.copy(alpha = 0.5f)
                        )
                    )
                }
                Text(
                    text = "The default rate is used when employees clock in.",
                    fontSize = 12.sp,
                    color = MaterialTheme.colorScheme.secondary,
                    modifier = Modifier.fillMaxWidth()
                )

                Spacer(modifier = Modifier.height(16.dp))

                DialogButtonRow(
                    confirmButtonText = "Save",
                    confirmEnabled = description.isNotBlank() && rateCode.isNotBlank(),
                    onConfirmClick = { onSave(description.trim(), rateCode.trim(), overtimeRateCode.trim(), defaultRate) },
                    dismissButtonText = "Cancel",
                    onDismissClick = onDismiss
                )
            }
        }
    }
}
